/**
 * Base option set utilities - extracted to break circular dependencies
 * between optionSetTools.ts and other option set files.
 */
import type { DocOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";

/**
 * Determines if an optionSet is a "boolean" option set. A boolean option set is one with exactly
 * the values "true" and "false".
 */
export function isBooleanOptionSet(optionSet?: DocOptionSet): boolean {
  const options = optionSet?.options;
  if (!options) {
    return false;
  }
  return Object.keys(options).length === 2 && "true" in options && "false" in options;
}

/**
 * Display-only: an opt-in (default-"false") boolean toggle left off is not an included feature and
 * should not be listed in option summaries. Default-"true" toggles are always shown — on (kept) or
 * off (a buyer-visible removal).
 */
export function isUninterestingBaseOption(optionSet: DocOptionSet | undefined, selectedOptionId: string): boolean {
  return isBooleanOptionSet(optionSet) && optionSet?.defaultValue === "false" && selectedOptionId === "false";
}

/**
 * Gets the linked option choices for an intersecting option set.
 * @param linkedOptionSet The intersecting option set to get the linked option sets for
 * @param optionId The option id to get the linked option choices for
 * @returns A record of option set ids to option ids that are linked to the intersecting option set
 */
export function getLinkedOptionChoices(linkedOptionSet: DocOptionSet, optionId: string): Record<string, string> {
  if (linkedOptionSet.optionSetType !== "INTERSECTING") {
    return {};
  }
  const linkedOptionChoices = {};
  const choices = linkedOptionSet.conditions[optionId].choices;
  for (const optionSetId in choices) {
    linkedOptionChoices[optionSetId] = choices[optionSetId][0];
  }
  return linkedOptionChoices;
}

export function optionSetOptionIds(optionSet: DocOptionSet | undefined, filterOutDisabled: boolean = false): string[] {
  return Object.keys(optionSet?.options ?? {}).filter(
    (optionId) => !filterOutDisabled || !optionSet?.options?.[optionId]?.disabled,
  );
}

/**
 * Gets the default option value for an option set.
 * For InstanceOptionSet, resolves the archetype's default value.
 * @param optionSet The option set to get the default from
 * @param optionSets Optional table of all option sets, needed to resolve archetype for instances
 */
export function optionSetDefaultOptionId(
  optionSet: DocOptionSet | null,
  _optionSets: Table<DocOptionSet>, // TODO: add this when InstanceOptionSet is implemented
): string | null {
  if (!optionSet) {
    return null;
  }

  // TODO: add this when InstanceOptionSet is implemented
  // if (optionSet.optionSetType === "INSTANCE") {
  //   const archetype = optionSets?.[optionSet.archetypeId];
  //   return archetype ? optionSetDefaultOptionId(archetype, optionSets) : null;
  // }

  return optionSet.defaultValue ?? null;
}

export function hasEnabledDefaultOption(optionSet: DocOptionSet, optionSets: Table<DocOptionSet>): boolean {
  const defaultValue = optionSetDefaultOptionId(optionSet, optionSets);
  const baseOption = defaultValue ? optionSet.options[defaultValue] : undefined;
  return !!baseOption && !baseOption.disabled;
}
